import { useState } from "react";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  SearchableSelect,
  Stack,
  TextField,
} from "~/components/ui";
import { TaxonSearchField } from "~/features/plants/TaxonSearchField";
import {
  CONTRIBUTABLE_RANKS,
  RANK_LABELS,
  useContributeTaxon,
  type TaxonSearchResult,
} from "~/state/taxonomy";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import type { TaxonRank } from "~/api/types";
import { formatScientificName } from "~/utils/taxonomy";

const rankOptions = CONTRIBUTABLE_RANKS.map((rank) => ({
  value: rank,
  label: RANK_LABELS[rank],
}));

/** Ranks that only make sense hanging off a parent taxon. */
const REQUIRES_PARENT: TaxonRank[] = [
  "genus",
  "species",
  "subspecies",
  "variety",
  "form",
  "hybrid",
  "cultivar",
];

export function ContributeTaxonForm({
  initialName = "",
  onCreated,
  onCancel,
}: {
  initialName?: string;
  onCreated: (taxon: TaxonSearchResult) => void;
  onCancel?: () => void;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const [rank, setRank] = useState<TaxonRank>("species");
  const [name, setName] = useState(initialName);
  const [parent, setParent] = useState<TaxonSearchResult | null>(null);
  const [authorship, setAuthorship] = useState("");
  const [commonName, setCommonName] = useState("");
  const [nativeRange, setNativeRange] = useState("");
  const contribute = useContributeTaxon();

  const parentRequired = REQUIRES_PARENT.includes(rank);
  const preview = formatScientificName(
    rank,
    name,
    parent?.scientificName ?? null,
    rank === "hybrid",
  );
  const canSubmit =
    name.trim().length > 0 && (!parentRequired || Boolean(parent));

  async function submit() {
    const created = await contribute.mutateAsync({
      rank,
      name,
      parentId: parent?.id ?? null,
      authorship,
      commonName,
      nativeRange,
    });

    onCreated({
      id: created.id,
      rank: created.rank,
      name: created.name,
      scientificName: created.scientific_name,
      authorship: authorship.trim() || null,
      commonName: commonName.trim() || null,
      familyName: parent?.familyName ?? null,
      genusName: parent?.genusName ?? null,
      verified: false,
    });
  }

  return (
    <Stack gap="md">
      <AppText size="xs">
        Anything you add here joins the shared botanical index so other growers
        can find it. It stays marked as community-contributed until it's
        verified against a botanical authority.
      </AppText>

      <View style={stylesheet.field}>
        <AppText size="xs" weight="semibold">
          Rank
        </AppText>
        <SearchableSelect
          title="Choose a rank"
          accessibilityLabel="Rank"
          value={rank}
          options={rankOptions}
          placeholder="Species"
          onValueChange={(value) => setRank(value as TaxonRank)}
        />
      </View>

      <View style={stylesheet.field}>
        <AppText size="xs" weight="semibold">
          {parentRequired ? "Sits under" : "Sits under (optional)"}
        </AppText>
        <TaxonSearchField
          selected={parent}
          onSelect={setParent}
          onClear={() => setParent(null)}
        />
      </View>

      <View style={stylesheet.field}>
        <AppText size="xs" weight="semibold">
          {rank === "cultivar" ? "Cultivar name" : "Name at this rank"}
        </AppText>
        <TextField
          accessibilityLabel="Name at this rank"
          value={name}
          placeholder={rank === "cultivar" ? "Thai Constellation" : "deliciosa"}
          autoCapitalize="none"
          autoCorrect={false}
          onChangeText={setName}
        />
      </View>

      {preview ? (
        <View style={stylesheet.preview}>
          <AppText size="xs">Will be saved as</AppText>
          <AppText weight="semibold">{preview}</AppText>
        </View>
      ) : null}

      <View style={stylesheet.field}>
        <AppText size="xs" weight="semibold">
          Common name (optional)
        </AppText>
        <TextField
          accessibilityLabel="Common name"
          value={commonName}
          placeholder="Swiss cheese plant"
          onChangeText={setCommonName}
        />
      </View>

      <View style={stylesheet.field}>
        <AppText size="xs" weight="semibold">
          Botanical authority (optional)
        </AppText>
        <TextField
          accessibilityLabel="Botanical authority"
          value={authorship}
          placeholder="Liebm."
          autoCapitalize="none"
          autoCorrect={false}
          onChangeText={setAuthorship}
        />
      </View>

      <View style={stylesheet.field}>
        <AppText size="xs" weight="semibold">
          Native range (optional)
        </AppText>
        <TextField
          accessibilityLabel="Native range"
          value={nativeRange}
          placeholder="Southern Mexico to Panama"
          onChangeText={setNativeRange}
        />
      </View>

      {contribute.error ? (
        <AppText size="xs" tone="danger">
          {contribute.error instanceof Error
            ? contribute.error.message
            : "Unable to save this name."}
        </AppText>
      ) : null}

      <View style={stylesheet.actions}>
        <Button
          iconName="plus"
          disabled={!canSubmit || contribute.isPending}
          onPress={() => void submit()}
        >
          <ButtonText>
            {contribute.isPending ? "Saving..." : "Add to the index"}
          </ButtonText>
        </Button>
        {onCancel ? (
          <Button variant="outline" onPress={onCancel}>
            <ButtonText>Cancel</ButtonText>
          </Button>
        ) : null}
      </View>
    </Stack>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    field: { gap: spacing.xs },
    preview: {
      gap: 2,
      padding: spacing.m,
      borderRadius: radii.m,
      backgroundColor: colors.surface,
    },
    actions: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
    },
  });
